import { jsx as _jsx } from "react/jsx-runtime";
import { Monitor, Moon, Sun } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { Button } from "../components/button.js";
import { Tooltip } from "../components/overlay.js";
const STORAGE_KEY = 'aeris.theme';
const ThemeContext = createContext(undefined);
const readStoredPreference = () => {
    if (typeof window === 'undefined')
        return 'system';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
};
const systemTheme = () => typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
export const ThemeProvider = ({ children, }) => {
    const [preference, setPreferenceState] = useState(readStoredPreference);
    const [systemResolved, setSystemResolved] = useState(systemTheme);
    // Follow the operating system live while the preference is `system`.
    useEffect(() => {
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (event) => {
            setSystemResolved(event.matches ? 'dark' : 'light');
        };
        query.addEventListener('change', onChange);
        return () => {
            query.removeEventListener('change', onChange);
        };
    }, []);
    const resolved = preference === 'system' ? systemResolved : preference;
    useEffect(() => {
        const root = document.documentElement;
        if (preference === 'system') {
            // Removing the attribute hands control back to the media query in
            // tokens.css, so there is exactly one source of truth.
            root.removeAttribute('data-theme');
        }
        else {
            root.setAttribute('data-theme', preference);
        }
    }, [preference]);
    const setPreference = useCallback((next) => {
        setPreferenceState(next);
        if (next === 'system')
            window.localStorage.removeItem(STORAGE_KEY);
        else
            window.localStorage.setItem(STORAGE_KEY, next);
    }, []);
    const toggle = useCallback(() => {
        setPreference(resolved === 'dark' ? 'light' : 'dark');
    }, [resolved, setPreference]);
    const value = useMemo(() => ({ preference, resolved, setPreference, toggle }), [preference, resolved, setPreference, toggle]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
};
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used inside a ThemeProvider');
    }
    return context;
};
const LABELS = {
    light: 'Tema claro',
    dark: 'Tema escuro',
    system: 'Tema do sistema',
};
/**
 * Cycles light -> dark -> system.
 *
 * The label always states the current theme, so the control is comprehensible
 * without seeing the icon — colour and shape are never the only signal
 * (docs/UI_UX.md).
 */
export const ThemeToggle = ({ className, }) => {
    const { preference, setPreference } = useTheme();
    const next = preference === 'light' ? 'dark' : preference === 'dark' ? 'system' : 'light';
    const Icon = preference === 'light' ? Sun : preference === 'dark' ? Moon : Monitor;
    return (_jsx(Tooltip, { content: LABELS[preference], side: "bottom", children: _jsx(Button, { variant: "ghost", size: "icon", "aria-label": `${LABELS[preference]}. Alternar para ${LABELS[next].toLowerCase()}`, onClick: () => {
                setPreference(next);
            }, ...(className === undefined ? {} : { className }), children: _jsx(Icon, { "aria-hidden": "true", className: "h-4 w-4" }) }) }));
};
//# sourceMappingURL=theme.js.map